import { getIdGenerator, getSingleRequestHandler } from "./skyward.base.extensions";
import { isAndroidApp, mobileAccess, virtualDir } from "./skyward.base.global";
import { formatServerDate } from "./skyward.base.time";
import { getScopeContainer, getValue, setValue } from "./skyward.ui.controls";
import debounce from "./skyward.ui.debounce";
import { registerEvents } from "./skyward.ui.events";
import { closePopup } from "./skyward.ui.window";
import { ClassicBrowse, CodeEelBinding, ModalLayer } from "./types";

const skyward = window.skyward;
const requestHandler = getSingleRequestHandler();
const idGenerator = getIdGenerator("popup", "_codeEelBrowse");

const events =
{
    init
};

registerEvents("codeEEL", events);

function init(selector: HTMLElement)
{
    const element = $(selector);
    if (!element.hasClass("eventsBound"))
    {
        registerCodeEEL(element);
    }
}

export function registerCodeEEL(codeEEL: JQuery<HTMLElement>)
{
    let lastValue: string | null; // stores the last searched value to prevent unnecessary searches
    let selectedValue: string | null = null;
    let layer: ModalLayer;

    type EelData =
        {
            codeEELCode: JQuery<HTMLElement>,
            codeEELCodeButton: JQuery<HTMLElement>,
            codeEELDescription: JQuery<HTMLElement>,
            codeEELSelectedValueDisplayField: JQuery<HTMLElement>,
            codeEELDescriptionButton: JQuery<HTMLElement>,
            codeEELValue: JQuery<HTMLElement>,
            filter: { [key: string]: string },
            postData: { [key: string]: string },
            tempTableName: string,
            additionalFieldsControls: { [key: string]: string },
            hidePrimaryBrowseButton: boolean,
            hideBrowseControl: boolean,
            layer: ModalLayer | null,
            codeEELValueData: { [key: string]: string | number },
        };

    const eelData: EelData =
    {
        codeEELCode: codeEEL.find(".codeEELCode"),
        codeEELCodeButton: codeEEL.find(".codeEELCodeButton"),
        codeEELDescription: codeEEL.find(".codeEELDescription"),
        codeEELSelectedValueDisplayField: codeEEL.find(".codeEELSelectedValueDisplayField"),
        codeEELDescriptionButton: codeEEL.find(".codeEELDescriptionButton"),
        codeEELValue: codeEEL.find(".codeEELValue"),
        filter: codeEEL.data("filter"),
        postData: codeEEL.data("postdata"),
        tempTableName: codeEEL.data("tempTableName"),
        additionalFieldsControls: codeEEL.data("additional-fields-controls"),
        hidePrimaryBrowseButton: codeEEL.data("hidePrimaryBrowseButton"),
        hideBrowseControl: codeEEL.data("hideBrowseControl"),
        layer: null,
        codeEELValueData: {}
    };
    const label = codeEEL.findLabel();
    if (label.length)
    {
        eelData.codeEELCode.attr("aria-label", label.text() + " code");
        eelData.codeEELDescription.attr("aria-label", label.text() + " description");
    }
    eelData.codeEELValueData = eelData.codeEELValue.data();

    skyward.eel.setFocusToTextBoxOnClick(codeEEL);

    codeEEL.on("click", ".codeEELButton:not(.ui-disabled):not(.mobile-eel-button)", function ()
    {
        // Set the selected id to the hidden value on load in case an id is already selected
        selectedValue = eelData.codeEELValue.val() as string;

        if (layer == undefined || layer == null || !layer.onscreen)
        {
            showPopup($(this).prev());
            doSearch();
        }
        else if (!skyward.global.mobileAccess)
        {
            layer.close();
        }
    });

    codeEEL.on("keydown", "input:not([disabled])", keydownEvent);

    addInitialInputEvent();

    codeEEL.addClass("eventsBound");

    function getBrowsePostData(textBox: JQuery<HTMLElement>)
    {
        type PostData =
            {
                filter: string,
                [key: string]: string
            };

        const postData: PostData =
        {
            filter: JSON.stringify(eelData.filter)
        };

        if (!textBox.data("dynamicfiltervalue"))
        {
            return $.extend({}, eelData.postData, postData);
        }

        const filterPostData: { [key: string ]: unknown } = {};
        type TextBoxData = {
            dynamicfilterfield: string,
            dynamicfiltervalue: string,
            dynamicfilterattribute: string,
        };
        const textBoxData = textBox.data() as TextBoxData;
        const dependentFields = textBoxData.dynamicfilterfield.split(",");
        const dependentValues = textBoxData.dynamicfiltervalue.split(",");
        const dependentAttributes = textBoxData.dynamicfilterattribute.split(",");
        const container = getScopeContainer(textBox);

        for (let i = 0; i < dependentFields.length; i++)
        {
            let dependentElement = container.find("[name='" + dependentValues[i] + "']");
            if (dependentElement.length === 0)
            {
                dependentElement = container.find("#" + dependentValues[i] + " .dmsBackingData");
            }

            const dependentStringArray = dependentAttributes[i].split("-");
            if (dependentStringArray.length >= 3 && dependentStringArray[2].length > 0)
            {
                const finalDependentString = dependentStringArray[1] + dependentStringArray[2].charAt(0).toUpperCase() + dependentStringArray[2].slice(1);

                if (typeof dependentElement.data(finalDependentString) !== "undefined")
                {
                    postData[dependentFields[i]] = dependentElement.data(finalDependentString);
                }
            }
            else
            {
                let value = getValue(dependentElement);
                if (value instanceof Date)
                {
                    value = formatServerDate(value, !dependentElement.hasClass("datepicker"));
                }
                postData[dependentFields[i]] = value;
            }
        }

        const dependentBrowsePostData: { [key: string]: unknown } = {};
        for (let key in eelData.postData)
        {
            const fieldName = key.substring(key.lastIndexOf(".") + 1);

            if (filterPostData[fieldName] && filterPostData[fieldName] != eelData.postData[key])
            {
                dependentBrowsePostData[key] = filterPostData[fieldName];
            }
            else
            {
                dependentBrowsePostData[key] = eelData.postData[key];
            }
        }

        return $.extend({}, dependentBrowsePostData, filterPostData, postData);
    }

    function closeOpenEelBrowse(browseId: string, textbox: JQuery<HTMLElement>)
    {
        eelData.layer = null;
        lastValue = null;

        requestHandler.closeOpenRequest();

        if (!mobileAccess)
        {
            textbox.trigger("focus");
        }

        addInitialInputEvent();

        delete skyward.browse.browseList[browseId];

        skyward.browse.openBrowses.splice(skyward.browse.openBrowses.indexOf(browseId), 1);

        textbox.trigger("CodeEelClosed");
    }

    function handleTabPress(browseId: string, textbox: JQuery<HTMLElement>)
    {
        if (codeEEL.is(".quickEntryText"))
        {
            closePopup();
            closeOpenEelBrowse(browseId, textbox);
            skyward.tabbing.focusAfter(textbox);
        }
        else
        {
            skyward.browse.handlePopupBrowseTabPress();
        }
    }

    function createModalLayer(browseId: string, textbox: JQuery<HTMLElement>)
    {
        const textBoxId = textbox.attr("id");
        const popupContent = $("<div>", { "class": "popup--browse", id: textBoxId + "_popup" });
        const browse = $("<div>", { id: browseId, "class": "browse browseStaticHeight" });
        popupContent.append(browse);
        const textBoxContainer = textbox.closest(".js-showEELContainer");

        skyward.browse.eventCallbacks.recordSelectionMade.registerCallback(selectionMadeHandler);

        textbox.attr("aria-expanded", "true");

        const bindings: CodeEelBinding<string>[] =
            [
                {
                    onEvents: "keydown",
                    bindTo: textbox,
                    runFunction: function (this: HTMLElement, event)
                    {
                        if (event.keyCode && event.keyCode == 9)
                        {
                            handleTabPress(browseId, $(this));
                            event.preventDefault();
                        }
                    }
                },
                {
                    // The Android app will trigger the input event whenever the value changes (even from code), we can't have this so we use the keyup event instead
                    onEvents: (isAndroidApp ? "keyup" : "input"),
                    bindTo: textbox,
                    runFunction: debounce(inputEvent)
                },
                {
                    onEvents: `rowChanged browseLoaded.${browseId}`,
                    bindTo: browse,
                    runFunction: function ()
                    {
                        const browse = skyward.browse.browseList[browseId]!;
                        const row = skyward.browse.getCurrentRowElement("unlocked", browseId, browse.currentRow!);
                        textbox.attr("aria-activedescendant", row.addIds().attr("id")!);
                    }
                }
            ];

        layer = skyward.modalLayer.showCodeEELDialog(popupContent, textbox, textBoxContainer, beforeClose, eelData, bindings);

        function beforeClose()
        {
            // If hidden value is not the same as the selected id then update
            if (selectedValue != eelData.codeEELValue.val())
            {
                eelData.codeEELValue.val(selectedValue!);
                eelData.codeEELValue.trigger("change");
            }

            if (selectedValue || codeEEL.data("keep-invalid-values") !== true)
            {
                updateBoxes(false);
            }

            skyward.browse.eventCallbacks.recordSelectionMade.deregisterCallback(selectionMadeHandler);

            closeOpenEelBrowse(browseId, textbox);

            textbox.attr("aria-activedescendant", null);
            textbox.attr("aria-expanded", "false");
        }

        function selectionMadeHandler(eventData?: { [key: string]: unknown })
        {
            if (eventData?.browseId === browseId)
            {
                selectedValue = eventData.selectedId as string;
            }
        }
    }

    function addToBrowseList(browseId: string, textbox: JQuery<HTMLElement>)
    {
        const isCode = textbox.hasClass("codeEELCode");
        skyward.browse.browseList[browseId] =
        {
            id: browseId,
            pageModule: eelData.codeEELValueData.area,
            pageObject: eelData.codeEELValueData.controller,
            displayName: eelData.codeEELValueData.displayName,
            pageScreen: textbox.data("action"),
            browseType: "Default",
            browseModule: eelData.codeEELValueData.area,
            browseObject: eelData.codeEELValueData.controller,
            dataModule: eelData.codeEELValueData.area, //TODO: PR 4339258 update to use actual dataModule instead of area
            dataObject: eelData.codeEELValueData.controller, //TODO: PR 4339258 update to use actual dataObject instead of controller
            browseName: "browse",
            selectMode: true,
            codeEelName: eelData.codeEELValue.attr("name"),
            codeEelDescription: eelData.codeEELDescription.attr("id"),
            hidePrimaryBrowseButton: eelData.hidePrimaryBrowseButton,
            routeHash: isCode ? eelData.codeEELValueData.codeRouteHash : eelData.codeEELValueData.descriptionRouteHash,
            browseId: isCode ? eelData.codeEELValueData.codeBrowseId : eelData.codeEELValueData.descriptionBrowseId,
            browseIDHash: isCode ? eelData.codeEELValueData.codeBrowseIdHash : eelData.codeEELValueData.descriptionBrowseIdHash,
            hideBrowseControl: eelData.hideBrowseControl,
            refreshTimeout: -1,
            data: getBrowsePostData(textbox),
            keyEventsEnabled: true,
            renderViewButton: true,
            disableBrowseSort: true,
            hideOptionsButton: true,
            preventColumnResize: false,
            renderQuickFilter: false,
            queryParameterData: codeEEL.data("codeEelQueryParameterData")
        } as ClassicBrowse;

        skyward.browse.openBrowses.push(browseId);
    }

    function showPopup(textbox: JQuery<HTMLElement>)
    {
        const browseId = idGenerator.getNewId();
        addToBrowseList(browseId, textbox);
        createModalLayer(browseId, textbox);
    }

    function doSearch(searchText?: string)
    {
        const browse = skyward.browse.getInnerBrowse(eelData.layer!.container);

        if (browse === undefined || browse == null)
        {
            return;
        }

        const data =
        {
            codeEELSearchText: searchText,
            seekId: eelData.codeEELValue.val(),
            tempTableName: eelData.tempTableName
        };

        requestHandler.request = skyward.browse.performCodeEELSearch(browse.id, data);
    }

    function updateBoxes(fireChangeEvent: boolean)
    {
        type PostData =
            {
                securityHash: string,
                tempTableName: string,
                selectedId?: string,
                codeEELQueryParameterData?: string
            };

        const postData: PostData =
        {
            securityHash: eelData.codeEELValueData.securityHash as string,
            tempTableName: eelData.tempTableName
        };

        fireChangeEvent = fireChangeEvent != false ? true : false;

        if (selectedValue)
        {
            postData.selectedId = selectedValue;
        }

        if (codeEEL.data("codeEelQueryParameterData") != null)
        {
            postData.codeEELQueryParameterData = JSON.stringify(codeEEL.data("codeEelQueryParameterData"));
        }

        return $.ajax(
            {
                url: virtualDir + eelData.codeEELValueData.area + "/" + eelData.codeEELValueData.controller + "/codeeel/" + eelData.codeEELValue.val(),
                type: "POST",
                dataType: "JSON",
                data: postData,
                activityIndicator: { hideGlass: true },
                success: function (data)
                {
                    populateBoxes(codeEEL, data, fireChangeEvent);

                    if (data.additionalFields)
                    {
                        codeEEL.data("additionalFields", data.additionalFields);
                    }

                    if (data.deniedAdditionalFields)
                    {
                        codeEEL.data("deniedFields", data.deniedAdditionalFields);
                    }

                    if (!eelData.layer || eelData.layer.closing)
                    {
                        codeEEL.trigger("eelComplete");
                    }

                    updateAdditionalFieldsData(codeEEL, eelData.additionalFieldsControls, data.additionalFields);
                }
            }
        );
    }

    function keydownEvent(this: HTMLElement, event: JQuery.KeyDownEvent)
    {
        if (eelData.layer != null)
        {
            return;
        }

        if (event.keyCode != 39 && event.keyCode != 40 && event.keyCode != 13) // Right arrow, down arrow and enter perform a search
        {
            return;
        }

        const textbox = $(this);
        if (mobileAccess && textbox.is(":animated"))
        {
            return;
        }
        showPopup(textbox);
        doSearch();
        selectedValue = null;
        textbox.trigger("focus");
    }

    function inputEvent(this: HTMLElement)
    {
        const textbox = $(this);
        const textboxValue = textbox.val() as string;

        if (skyward.global.mobileAccess && textbox.is(":animated"))
        {
            return;
        }

        if (lastValue != null && textboxValue.toLowerCase() === lastValue.toLowerCase())
        {
            return;
        }

        if (eelData.layer == null)
        {
            showPopup(textbox);
        }

        doSearch(textboxValue);
        lastValue = textbox.val() as string;
        selectedValue = null;
        textbox.trigger("focus");
    }

    function addInitialInputEvent()
    {
        // This event is only added to be fired once, so the debounced event attached through the modal layer can take over
        // This event gets re-attached when the modal layer closes
        // The Android app will trigger the input event whenever the value changes (even from code), we can't have this so we use the keyup event instead
        codeEEL.one((isAndroidApp ? "keyup" : "input"), "input:not([disabled])", inputEvent);
    }
};

function fireChangeEvents(codeeel: JQuery<HTMLElement>)
{
    codeeel.find(".codeEELValue").trigger("change");
    if (codeeel.data("keep-invalid-values") === true)
    {
        codeeel.find(".codeEELCode").trigger("change");
        codeeel.find(".codeEELDescription").trigger("change");
    }
}

function updateAdditionalFieldsData(codeEEL: JQuery<HTMLElement>, codeEELAdditionalFieldsControls: { [key: string]: string } | null, additionalFields: { [key: string]: string } | null)
{
    if (codeEEL)
    {
        if (!codeEELAdditionalFieldsControls)
        {
            codeEELAdditionalFieldsControls = codeEEL.data("additional-fields-controls");
        }

        if (codeEELAdditionalFieldsControls)
        {
            const parentContainer = getScopeContainer(codeEEL);

            if (parentContainer.length > 0)
            {
                $.each(codeEELAdditionalFieldsControls, function (id, control)
                {
                    let value = null;
                    if (additionalFields)
                    {
                        const additionalFieldValue = additionalFields[id as string];
                        if (additionalFieldValue)
                        {
                            value = additionalFieldValue;
                        }
                    }

                    const nameElement = parentContainer.find("[name='" + JSON.parse(control) + "']");
                    setValue(nameElement, value);
                });
            }
        }
    }
};

export function populateBoxes(codeEEL: JQuery<HTMLElement>, ajaxData: { [key: string]: unknown }, fireChangeEvent?: boolean)
{
    codeEEL.find(".codeEELCode").val(ajaxData.code as string || "");
    codeEEL.find(".codeEELDescription").val(ajaxData.description as string || "");
    codeEEL.find(".codeEELSelectedValueDisplayField").val(ajaxData.selectedValueDispayData as string || "");
    codeEEL.trigger("codeEELValuesUpdated");
    if (fireChangeEvent)
    {
        fireChangeEvents(codeEEL);
        updateAdditionalFieldsData(codeEEL, null, ajaxData.additionalFields as { [key: string]: string });
    }

    //Quick Entry Text needs to fire the dirty call again if a value was selected from the code EEL browse and not typed in.
    if (codeEEL.closest(".quickEntryText").length !== 0)
    {
        codeEEL.find(".codeEELDescription").trigger("dirty");
    }
}

export function clear(codeEEL: JQuery<HTMLElement>, fireChangeEvent?: boolean)
{
    codeEEL.find(".codeEELCode").val("");
    codeEEL.find(".codeEELDescription").val("");
    codeEEL.find(".codeEELSelectedValueDisplayField").val("");
    codeEEL.find(".codeEELValue").val("");
    codeEEL.trigger("codeEELValuesUpdated");
    if (fireChangeEvent)
    {
        fireChangeEvents(codeEEL);
    }
}

export function getQueryParameterData(codeEEL: JQuery<HTMLElement>)
{
    let queryParameterData = codeEEL.data("codeEelQueryParameterData");

    if (typeof queryParameterData === "string")
    {
        try
        {
            queryParameterData = JSON.parse(queryParameterData);
        }
        catch (e)
        {
            queryParameterData = {};
        }
    }

    return $.extend({}, queryParameterData);
}

export function setQueryParameterData(codeEEL: JQuery<HTMLElement>, queryParameterData: { [key: string]: string } | string)
{
    let newQueryParameterData = queryParameterData;

    if (typeof newQueryParameterData === "string" && newQueryParameterData !== "")
    {
        newQueryParameterData = JSON.parse(newQueryParameterData);
    }
    else if (typeof newQueryParameterData !== "object")
    {
        newQueryParameterData = "";
    }

    codeEEL.data("codeEelQueryParameterData", newQueryParameterData);
}

export function setCodeEELValue(codeEEL: JQuery<HTMLElement>, value: any, suppressChangeEvent = false, updateCodeAndDescription = true)
{
    const codeEELHiddenInput = codeEEL.find(".codeEELValue");

    codeEELHiddenInput.val(value);

    if (updateCodeAndDescription)
    {
        const codeEELData = codeEELHiddenInput.data();
        const postData =
        {
            "securityHash": codeEELData.securityHash,
            "tempTableName": codeEEL.data("tempTableName")
        };

        $.ajax(
            {
                url: virtualDir + codeEELData.area + "/" + codeEELData.controller + "/codeeel/" + value,
                type: "POST",
                data: postData,
                success: function (data)
                {
                    populateBoxes(codeEEL, data, !suppressChangeEvent);
                }
            });
    }
}